/* AddEditConnectionRenderer.js */

"use strict";

const _ = require("lodash");
const uiCore = require("@gluenetworks/steve_ui_core");
const Forms = require("@gluenetworks/forms");
const FormsComponent = Forms.ui.component;
const BaseRefResolver = Forms.resolvers.ref.base;
const ButtonControl = uiCore.Control.Action.Button;
const ComboBoxControl = uiCore.Control.OptionSelection.Single.ComboBox;
const InputTextControl = uiCore.Control.Textfield.InputText;
const TextareaControl = uiCore.Control.Textfield.Textarea;
const InputRadioControl = uiCore.Control.OptionSelection.Single.InputRadio;
const CheckboxControl = uiCore.Control.Boolean.InputCheckbox;
const MODAL_TITLE_ADD = "Add Connection";
const MODAL_TITLE_EDIT = "Edit Connection";
const MODAL_TITLE_VIEW = "View Connection";

/**
 * @class AddEditConnectionRenderer
 *
 * Responsible for rendering the UI for adding and editing service connections.
 *
 * @listens AddEditConnectionComponent#event:Data - Handles updates to connection data.
 * @listens AddEditConnectionComponent#event:DirtyStateChanged - Handles changes in the dirty state of the connection.
 * @listens ComboBoxControl#event:UserValue - Handles user selection of connection type.
 * @listens InputTextControl#event:UserValue - Handles user input in text fields.
 * @listens ButtonControl#event:UserClick - Handles user clicks on buttons.
 * @listens FormsComponent#event:PendingPatchesChanged - Handles form pending patches.
 */
class AddEditConnectionRenderer {
    /**
     * Creates an instance of AddEditConnectionRenderer.
     *
     * @param {AddEditConnectionComponent} model - The model associated with the renderer.
     */
    constructor(model) {
        this._model = model;

        this._rootElem = this._createRootElement();

        const refResolver = new BaseRefResolver(
            this._model._manageConnectionComponent._nibApiService,
            this._model._manageConnectionComponent._userSession,
            true
        );
        this._formsComponent = new FormsComponent(refResolver, undefined, false);

        /* form controls */

        this._selectConnectionType = new ComboBoxControl({
            showFilter: false,
            adjustWidth: true,
            darkTheme: true,
        });

        this._cancelButton = new ButtonControl({
            label: "Cancel",
            title: "Cancel",
            darkTheme: true,
            disabled: false,
        });

        this._closeButton = new ButtonControl({
            label: "Close",
            title: "Close",
            darkTheme: true,
            disabled: false,
        });

        this._confirmButton = new ButtonControl({
            label: "Save",
            title: "Save",
            darkTheme: true,
            disabled: true,
            active: true,
        });

        this._testConnectionButton = new ButtonControl({
            label: "Test Connection",
            title: "Test Connection",
            darkTheme: true,
            disabled: true,
        });

        this._connectionNameControl = new InputTextControl({
            darkTheme: true,
            placeholder: "Name",
        });

        this._connectionNameErrorElement = document.createElement("div");
        this._connectionNameErrorElement.classList.add("error", "hidden", "name-control-error");
        this._connectionNameErrorElement.innerText = "Name is required";

        this._descriptionControl = new InputTextControl({
            darkTheme: true,
            placeholder: "Description",
        });

        this._advancedTextareaControl = new TextareaControl({
            darkTheme: true,
            rows: 10,
        });

        this._advancedRadioButtonControl = new InputRadioControl({
            darkTheme: true,
            horizontal: true,
            options: [
                {
                    label: "Basic",
                    tooltip: "Enter configuration in the form",
                    value: false,
                },
                {
                    label: "Advanced",
                    tooltip: "Enter configuration as a JSON object",
                    value: true,
                },
            ],
            value: false,
        });

        this._sharedCheckboxControl = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Share with subordinate organizations",
            title: "Allows subordinate organizations to see and use this connection.",
        });

        this._onFormsPendingPatchesChanged = _.debounce(
            this._onFormsPendingPatchesChanged.bind(this),
            500,
            {leading: false, trailing: true}
        );
        this._testConnectionClick = this._testConnectionClick.bind(this);

        this.render();
    }

    get rootElement() {
        return this.getRootElement();
    }

    get model() {
        return this.getModel();
    }

    /*******************************************/
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    render() {
        if (this._renderProm === null) {
            return Promise.resolve();
        }
        this._renderProm = this._renderProm || this._render();
        return this._renderProm;
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    getRootElement() {
        return this._rootElem;
    }

    getModel() {
        return this._model;
    }

    /********************************************/
    /* END PUBLIC COMPONENT RENDERER METHODS    */
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on("Data", this._onData.bind(this));
        this._model.on("DirtyStateChanged", this._onDirtyStateChanged.bind(this));

        this._selectConnectionType.on("UserValue", this._onSelectConnectionType.bind(this));
        this._connectionNameControl.on("UserValue", this._onNameChange.bind(this));
        this._confirmButton.on("UserClick", this._confirmClick.bind(this));
        this._cancelButton.on("UserClick", this._onCancelClick.bind(this));
        this._closeButton.on("UserClick", this._onCancelClick.bind(this));
        this._testConnectionButton.on("UserClick", this._testConnectionClick);
        this._formsComponent.on("PendingPatchesChanged", this._onFormsPendingPatchesChanged);
        this._descriptionControl.on("UserValue", this._onDescriptionChange.bind(this));
        this._sharedCheckboxControl.on("UserValue", this._onSharedChange.bind(this));
        this._advancedTextareaControl.on(
            "UserValue",
            (this._onAdvancedTextareaChange = this._onAdvancedTextareaChange.bind(this))
        );
        this._advancedRadioButtonControl.on(
            "UserValue",
            (this._onAdvancedRadioButtonChange = this._onAdvancedRadioButtonChange.bind(this))
        );
    }

    _unregisterListeners() {
        this._model.removeListener("Data", this._onData);
        this._model.removeListener("DirtyStateChanged", this._onDirtyStateChanged);

        this._selectConnectionType.removeAllListeners("UserValue");
        this._connectionNameControl.removeAllListeners("UserValue");
        this._confirmButton.removeAllListeners("UserClick");
        this._cancelButton.removeAllListeners("UserClick");
        this._closeButton.removeAllListeners("UserClick");
        this._testConnectionButton.removeAllListeners("UserClick");
        this._formsComponent.removeListener(
            "PendingPatchesChanged",
            this._onFormsPendingPatchesChanged
        );
        this._descriptionControl.removeAllListeners("UserValue");
        this._sharedCheckboxControl.removeAllListeners("UserValue");
        this._advancedTextareaControl.removeAllListeners("UserValue");
        this._advancedRadioButtonControl.removeListener(
            "UserValue",
            this._onAdvancedRadioButtonChange
        );
    }

    _render() {
        const root = document.createDocumentFragment();

        this._model._copyFrom = this._model._copyFrom || [];

        root.appendChild(this._createForm());
        this._rootElem.appendChild(root);
        this._setFormTypeInfo();
        this._setPristineProperties();
        if (this._model._isInherited) {
            this._setDisabled();
            this._cancelButton.renderer.getRootElement().classList.add("hidden");
            this._closeButton.renderer.getRootElement().classList.remove("hidden");
            this._confirmButton.renderer.getRootElement().classList.add("hidden");
            this._testConnectionButton.setDisabled(false);
            this._onFocusIn = (e) => e.target.blur();
            this._rootElem.addEventListener("focusin", this._onFocusIn);
        } else if (this._model._isSystemManaged) {
            this._setDisabledExceptShared();
        }
        this._formsComponent.renderer.render();
        this._registerListeners();
    }

    _shutdown() {
        this._unregisterListeners();
        if (this._onFocusIn) {
            this._rootElem.removeEventListener("focusin", this._onFocusIn);
            this._onFocusIn = undefined;
        }
        this._rootElem.remove();

        return Promise.all([
            this._cancelButton.shutdown(),
            this._closeButton.shutdown(),
            this._confirmButton.shutdown(),
            this._connectionNameControl.shutdown(),
            this._descriptionControl.shutdown(),
            this._sharedCheckboxControl.shutdown(),
            this._advancedTextareaControl.shutdown(),
            this._advancedRadioButtonControl.shutdown(),
            this._testConnectionButton.shutdown(),
        ])
            .then(() => {
                this._model = null;
                this._shutdownProm = null;
                this._renderProm = null;
                this._clickHandler = undefined;
                this._columns = undefined;
                this._cancelButton = undefined;
                this._closeButton = undefined;
                this._confirmButton = undefined;
                this._testConnectionButton = undefined;
                this._connectionNameControl = undefined;
                this._descriptionControl = undefined;
                this._sharedCheckboxControl = undefined;
                this._selectConnectionType = undefined;
                this._controlButtonsContainer = undefined;
                this._advancedTextareaControl = undefined;
                this._advancedRadioButtonControl = undefined;
                this._advContainer = undefined;
                this._basicContainer = undefined;
                this._sharedContainer = undefined;
                this._nameContainer = undefined;
                this._descriptionContainer = undefined;
                this._jtypeFormContainer = undefined;
                this._formsComponent = undefined;
                this._rootElem = undefined;
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PRIVATE RENDERER METHODS          */
    /*******************************************/

    _createRootElement() {
        const rootElement = document.createElement("div");
        rootElement.classList.add("add-edit-connection-parent");
        rootElement.component = this._model;
        return rootElement;
    }

    _createForm() {
        const formRoot = document.createElement("div");
        formRoot.classList.add("add-edit-connection", "modal_feedback_container");

        const formRootFragment = document.createDocumentFragment();
        formRootFragment.appendChild(formRoot);

        const formContainer = document.createElement("connection-form-container");
        const title = this._model._isInherited
            ? MODAL_TITLE_VIEW
            : this._model._isNewConnection
            ? MODAL_TITLE_ADD
            : MODAL_TITLE_EDIT;
        formContainer.innerHTML = `<h2>${title}</h2>`;

        const sharedContainer = document.createElement("div");
        sharedContainer.classList.add("shared-container");
        sharedContainer.appendChild(this._sharedCheckboxControl.renderer.getRootElement());
        formContainer.appendChild(sharedContainer);
        this._sharedContainer = sharedContainer;

        const nameLabel = document.createElement("label");
        nameLabel.innerText = "Name";
        const nameContainer = document.createElement("div");
        nameContainer.appendChild(nameLabel);

        const formActionContainer = document.createElement("actions-container");

        const formActionButtonsContainer = document.createElement("div");
        formActionButtonsContainer.classList.add("control-buttons");
        formActionButtonsContainer.appendChild(this._cancelButton.renderer.getRootElement());
        this._closeButton.renderer.getRootElement().classList.add("hidden");
        formActionButtonsContainer.appendChild(this._closeButton.renderer.getRootElement());
        formActionButtonsContainer.appendChild(this._confirmButton.renderer.getRootElement());

        const formLeftActionButtonsContainer = document.createElement("div");
        formLeftActionButtonsContainer.classList.add("left-control-buttons", "control-buttons");
        formLeftActionButtonsContainer.appendChild(
            this._testConnectionButton.renderer.getRootElement()
        );
        formActionContainer.appendChild(formLeftActionButtonsContainer);

        const connectionNameControlContainer = document.createElement("div");
        connectionNameControlContainer.appendChild(
            this._connectionNameControl.renderer.getRootElement()
        );
        connectionNameControlContainer.appendChild(this._connectionNameErrorElement);
        nameContainer.appendChild(connectionNameControlContainer);
        this._nameContainer = nameContainer;

        const descriptionContainer = document.createElement("div");
        descriptionContainer.innerHTML = `
            <label>Description</label>
        `;
        descriptionContainer.appendChild(this._descriptionControl.renderer.getRootElement());
        this._descriptionContainer = descriptionContainer;

        this._selectConnectionType.setOptions(this._model._typeOptions);

        const selectTypeContainer = document.createElement("div");
        selectTypeContainer.classList.add("select-type-container");
        selectTypeContainer.innerHTML = `
            <div class="select-type-inner">
                <label title="Select Connection type">Select Connection type</label>
            </div>
        `;
        const selectTypeInner = selectTypeContainer.querySelector(".select-type-inner");
        selectTypeInner.appendChild(this._selectConnectionType.renderer.getRootElement());

        const basicContainer = document.createElement("div");
        basicContainer.classList.add("basic-container");
        basicContainer.appendChild(this._formsComponent.renderer.rootElement);
        this._basicContainer = basicContainer;

        const advContainer = document.createElement("div");
        advContainer.classList.add("advanced-container");
        advContainer.appendChild(this._advancedTextareaControl.renderer.getRootElement());
        this._advContainer = advContainer;

        const jtypeFormContainer = document.createElement("div");
        jtypeFormContainer.classList.add("jtype-form-container");
        jtypeFormContainer.appendChild(selectTypeContainer);
        jtypeFormContainer.appendChild(this._advancedRadioButtonControl.renderer.getRootElement());
        jtypeFormContainer.appendChild(basicContainer);
        jtypeFormContainer.appendChild(advContainer);
        this._jtypeFormContainer = jtypeFormContainer;
        formContainer.appendChild(nameContainer);
        formContainer.appendChild(descriptionContainer);
        formContainer.appendChild(jtypeFormContainer);
        formActionContainer.appendChild(formActionButtonsContainer);

        formRoot.appendChild(formContainer);
        formRootFragment.appendChild(formActionContainer);

        return formRootFragment;
    }

    _setDisabled() {
        this._sharedContainer.classList.add("readonly");
        this._nameContainer.classList.add("readonly");
        this._descriptionContainer.classList.add("readonly");
        this._jtypeFormContainer.classList.add("readonly");
    }

    _setDisabledExceptShared() {
        this._nameContainer.classList.add("readonly");
        this._descriptionContainer.classList.add("readonly");
        this._jtypeFormContainer.classList.add("readonly");
    }

    _setFormTypeInfo(id) {
        const connectionTypeId =
            id || this._model._connectionEntity.connectionTypeId || this._model._typeEntities[0].id;
        const connectionTypeInfo = this._model._typeOptJtypeMap[connectionTypeId];

        if (connectionTypeInfo) {
            this._formsComponent.setTypeInformation(connectionTypeInfo, true);
        }
    }

    _setPristineProperties() {
        if (!this._model._isNewConnection) {
            const pristineValues = this._model._connectionEntity._pristineProperties;
            this._setFormProperties(pristineValues);
        } else {
            this._setFormProperties({
                connectionTypeId: this._model._typeEntities[0].id,
            });
            this._model._connectionEntity.setConnectionTypeId(this._model._typeEntities[0].id);
        }
    }

    _setFormProperties(data) {
        this._connectionNameControl.setValue(data.name);
        this._descriptionControl.setValue(data.description);
        this._sharedCheckboxControl.setValue(data.shared || false);
        this._selectConnectionType.setValue(data.connectionTypeId);
        this._advancedRadioButtonControl.setValue(data.advanced || false);
        if (data.advanced) {
            this._model._advancedConfig = data.connectionInformation;
        }
        this._onAdvancedRadioButtonChange(data.advanced || false);
        if (data.connectionInformation) {
            this._formsComponent.setPristineValue(data.connectionInformation);
        }
    }

    /*******************************************/
    /* END PRIVATE RENDERER METHODS            */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onData(data = {}) {
        this._setData(data);
    }

    _onDirtyStateChanged() {
        this.enableDisableConfirmButton();
    }

    _validateData() {
        const nameVal = this._connectionNameControl.getValue() || "";
        if (!nameVal.trim()) {
            this._connectionNameErrorElement.classList.remove("hidden");
            return false;
        } else {
            this._connectionNameErrorElement.classList.add("hidden");
        }
        if (this._advancedRadioButtonControl.getValue()) {
            if (!this._isJsonValid) {
                return false;
            }
        } else {
            if (this._formsComponent.isFormInvalid()) {
                return false;
            }
        }
        return true;
    }

    _onSelectConnectionType(id) {
        this._setFormTypeInfo(id, true);
        if (id === this._model._connectionEntity._pristineProperties.connectionTypeId) {
            const advanced = _.get(
                this._model,
                "_connectionEntity._pristineProperties.advanced",
                false
            );
            const pristineConfiguration = _.get(
                this._model,
                "_connectionEntity._pristineProperties.connectionInformation",
                {}
            );

            this._setAdvancedRadioButtonControl(advanced);
            this._formsComponent.setPristineValue(pristineConfiguration, true);
            this._advancedTextareaControl.setValue(
                JSON.stringify(pristineConfiguration, undefined, 4)
            );
            this._model._advancedConfig = pristineConfiguration;
        } else {
            this._model._advancedConfig = {};
            this._advancedTextareaControl.setValue(
                JSON.stringify(this._model._advancedConfig, undefined, 4)
            );
            this._setAdvancedRadioButtonControl(false);
            this._formsComponent.setPristineValue({}, true);
        }
        this._formsComponent.renderer.render().then(() => {
            this._model._connectionEntity.setConnectionTypeId(id);
            this._model._connectionEntity.setConnectionInformation(this._formsComponent.getValue());
        });
    }

    _onNameChange(value) {
        this._model._connectionEntity.setName(value);
        this.enableDisableConfirmButton();
    }

    _onDescriptionChange(val) {
        this._model._connectionEntity.setDescription(val);
    }

    _onSharedChange(val) {
        const pristineShared = this._model._connectionEntity._pristineProperties.shared;
        const nextValue = !val && !pristineShared ? pristineShared : val;
        this._model._connectionEntity.setShared(nextValue);
    }

    _onAdvancedTextareaChange(val) {
        try {
            let configObj = JSON.parse(val);
            this._model._advancedConfig = configObj;
            this._model._connectionEntity.setConnectionInformation(configObj);
            this._isJsonValid = true;
        } catch (e) {
            this._isJsonValid = false;
        } finally {
            this.enableDisableConfirmButton();
        }
    }

    _doAdvancedRadioButtonChange(val) {
        this._showHideAdvancedConfig(val);
        this._model._connectionEntity.setAdvanced(val);
        if (val) {
            const formData = this._formsComponent.getValue();
            _.merge(this._model._advancedConfig, formData);
            this._isJsonValid = true;
            this._advancedTextareaControl.setValue(
                JSON.stringify(this._model._advancedConfig, undefined, 4)
            );
            this._model._connectionEntity.setConnectionInformation(this._model._advancedConfig);
        } else {
            try {
                const advancedConfig = this._advancedTextareaControl.getValue() || "{}";
                const advancedConfigObj = JSON.parse(advancedConfig);
                this._model._advancedConfig = advancedConfigObj;
                this._formsComponent.setPristineValue(advancedConfigObj);
                this._isJsonValid = true;
            } catch (e) {
                console.log(e);
            }
        }
        this.enableDisableConfirmButton();
    }

    _onAdvancedRadioButtonChange(val = false) {
        if (val === false && this._model._connectionEntity.getAdvanced() === true) {
            this.model._uiUtils
                .showDeleteConfirmDialog({
                    message:
                        "Advanced and Basic modes may be different and changes may be lost, continue?",
                    confirmButtonName: "Confirm",
                    cancelButtonName: "Cancel",
                })
                .then(() => {
                    this._doAdvancedRadioButtonChange(val);
                })
                .catch(() => {
                    const advModeControl = this._advancedRadioButtonControl;
                    advModeControl.setValue(!advModeControl.getValue());
                });
        } else {
            this._doAdvancedRadioButtonChange(val);
        }
    }

    _onCancelClick() {
        this._model.closePopUp();
    }

    _onFormsPendingPatchesChanged(/*val*/) {
        if (this._model._connectionEntity.advanced) {
            return;
        }
        const connInfo = this._formsComponent.getValue();
        this._model._connectionEntity.setConnectionInformation(connInfo);
        this.enableDisableConfirmButton();
    }

    _confirmClick() {
        Promise.all([])
            .then(() => {
                this._model
                    .saveConnection()
                    .then(() => {
                        this.model.closePopUp();
                    })
                    .catch((err) => {
                        this.model.userFeedback.displayError(err);
                        console.log("Save Failed::", err);
                    });
            })
            .catch((err) => {
                this.model.userFeedback.displayError(err);
                console.log(err);
            });
    }

    _testConnectionClick() {
        this._testConnectionButton.setDisabled(true);
        const connectionInfo = this._model._connectionEntity.getConnectionInformation();
        const connectionTypeId = this._selectConnectionType.getValue();
        this._model
            .testConnection({connectionTypeId, connectionInfo})
            .then((response) => {
                if (response.summary.errorMsg) {
                    throw new Error(response.summary.errorMsg);
                }
                this._model._uiUtils.showMessageDialog({
                    message: "Connection Successful.",
                    confirmButtonName: "OK",
                });
                this._testConnectionButton.setDisabled(false);
            })
            .catch((err) => {
                this._model._uiUtils.showMessageDialog({
                    message: "Connection Failed.",
                    confirmButtonName: "OK",
                });
                this._testConnectionButton.setDisabled(false);
                console.log("Test Connection Failed::", err);
            });
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    enableDisableConfirmButton() {
        const isNotDirty = !this._model._connectionEntity.isDirty();
        const isFormInValid = !this._validateData();
        this._confirmButton.setDisabled(isNotDirty || isFormInValid);
        this._testConnectionButton.setDisabled(isFormInValid);
    }

    _showHideAdvancedConfig(val) {
        if (val) {
            this._advContainer.classList.remove("hidden");
            this._basicContainer.classList.add("hidden");
        } else {
            this._advContainer.classList.add("hidden");
            this._basicContainer.classList.remove("hidden");
        }
    }

    _setAdvancedRadioButtonControl(val) {
        this._advancedRadioButtonControl.setValue(val);
        this._onAdvancedRadioButtonChange(val);
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = AddEditConnectionRenderer;
